/* eslint-disable jsx-a11y/label-has-associated-control, jsx-a11y/control-has-associated-label */
import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import ImageUploader from '../ImageUploader';
import PuzzleConfigForm, { getGridRecommendations } from '../PuzzleConfigForm';
import { getCategories, uploadAdminImage } from '../../services/adminApi';
import { containerVariants, itemVariants } from '../../utils/animations';

function ImageUploadModal({ isOpen, onClose, onSuccess }) {
  const [uploadData, setUploadData] = useState({ title: '', description: '', imageData: null });
  const [imageDimensions, setImageDimensions] = useState(null);
  const [rows, setRows] = useState(3);
  const [columns, setColumns] = useState(3);
  const [pieceStyle, setPieceStyle] = useState('classic');
  const [imageUploaderKey, setImageUploaderKey] = useState(0);
  const [scheduleDate, setScheduleDate] = useState(null);
  const [allCategories, setAllCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categoryInput, setCategoryInput] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const cats = await getCategories();
        setAllCategories(cats);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch categories:', err);
      }
    };
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  const resetForm = () => {
    setUploadData({ title: '', description: '', imageData: null });
    setImageDimensions(null);
    setRows(3);
    setColumns(3);
    setPieceStyle('classic');
    setScheduleDate(null);
    setSelectedCategories([]);
    setCategoryInput('');
    setError(null);
    setImageUploaderKey((prev) => prev + 1);
  };

  const handleImageSelect = (data) => {
    if (data === null) {
      setUploadData((prev) => ({ ...prev, imageData: null }));
      setImageDimensions(null);
    } else if (typeof data === 'string') {
      setUploadData((prev) => ({ ...prev, imageData: data }));
      setImageDimensions(null);
    } else {
      setUploadData((prev) => ({ ...prev, imageData: data.imageData }));
      setImageDimensions({
        width: data.width,
        height: data.height,
        aspectRatio: data.aspectRatio,
      });
      const { recommended } = getGridRecommendations(data.aspectRatio);
      if (recommended.length > 0) {
        setRows(recommended[0].rows);
        setColumns(recommended[0].columns);
      }
    }
  };

  const handleUpload = async () => {
    if (!uploadData.title || !uploadData.imageData || !scheduleDate) {
      setError('Title, image, and schedule date are required');
      return;
    }

    try {
      setIsUploading(true);
      const dateStr = `${scheduleDate.getFullYear()}-${String(scheduleDate.getMonth() + 1).padStart(2, '0')}-${String(scheduleDate.getDate()).padStart(2, '0')}`;
      await uploadAdminImage(
        uploadData.imageData,
        uploadData.title,
        uploadData.description,
        { rows, columns, pieceStyle },
        dateStr,
        selectedCategories,
      );
      resetForm();
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose]);

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
      role="presentation"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
    >
      <motion.div
        className="bg-puzzle-surface rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col"
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        transition={{ type: 'spring', duration: 0.3, bounce: 0.2 }}
      >
        {/* Fixed Header */}
        <motion.div
          className="p-6 pb-4 border-b border-puzzle-border"
          variants={itemVariants}
          initial="hidden"
          animate="visible"
        >
          <h2 className="text-xl font-bold text-puzzle-text">Upload New Image</h2>
        </motion.div>

        {/* Error Banner */}
        {error && (
          <motion.div
            className="mx-6 mt-4 p-3 bg-puzzle-danger/10 border border-puzzle-danger/30 rounded-lg text-puzzle-danger text-sm"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {error}
          </motion.div>
        )}

        {/* Scrollable Content */}
        <motion.div
          className="flex-1 overflow-y-auto p-6 pt-4 space-y-4"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants}>
            <label htmlFor="title" className="block text-sm font-medium text-puzzle-text mb-1">
              Title *
            </label>
            <input
              type="text"
              id="title"
              value={uploadData.title}
              onChange={(e) => setUploadData((prev) => ({ ...prev, title: e.target.value }))}
              className="w-full px-3 py-2 border border-puzzle-border rounded-lg focus:ring-2 focus:ring-puzzle-blue focus:border-puzzle-blue"
              placeholder="e.g., Mountain Sunset"
            />
          </motion.div>

          <motion.div variants={itemVariants}>
            <label htmlFor="description" className="block text-sm font-medium text-puzzle-text mb-1">
              Description
            </label>
            <textarea
              id="description"
              value={uploadData.description}
              onChange={(e) => setUploadData((prev) => ({ ...prev, description: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-puzzle-border rounded-lg focus:ring-2 focus:ring-puzzle-blue focus:border-puzzle-blue"
              placeholder="Optional description..."
            />
          </motion.div>

          {/* Categories */}
          <motion.div variants={itemVariants}>
            <label className="block text-sm font-medium text-puzzle-text mb-1">
              Categories
            </label>

            {selectedCategories.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {selectedCategories.map((cat) => (
                  <span
                    key={cat}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-puzzle-blue/10 text-puzzle-blue text-sm rounded"
                  >
                    {cat}
                    <button
                      type="button"
                      onClick={() => setSelectedCategories((prev) => prev.filter((c) => c !== cat))}
                      className="text-puzzle-blue hover:opacity-70"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <input
                type="text"
                value={categoryInput}
                onChange={(e) => setCategoryInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && categoryInput.trim()) {
                    e.preventDefault();
                    const newCat = categoryInput.trim().toLowerCase();
                    if (!selectedCategories.includes(newCat)) {
                      setSelectedCategories((prev) => [...prev, newCat]);
                    }
                    setCategoryInput('');
                  }
                }}
                list="category-suggestions"
                placeholder="Type category and press Enter"
                className="flex-1 px-3 py-2 border border-puzzle-border rounded-lg focus:ring-2 focus:ring-puzzle-blue focus:border-puzzle-blue"
              />
              <datalist id="category-suggestions">
                {allCategories
                  .filter((c) => !selectedCategories.includes(c))
                  .map((cat) => (
                    <option key={cat} value={cat} />
                  ))}
              </datalist>
            </div>
            <p className="mt-1 text-xs text-puzzle-text-secondary">
              Press Enter to add. Click × to remove.
            </p>
          </motion.div>

          <motion.div variants={itemVariants}>
            <label className="block text-sm font-medium text-puzzle-text mb-1">
              Image *
            </label>
            <ImageUploader
              key={imageUploaderKey}
              onImageSelect={handleImageSelect}
              maxSizeMB={10}
            />
          </motion.div>

          {/* Puzzle Configuration - only show after image is selected */}
          {uploadData.imageData && (
            <PuzzleConfigForm
              imageData={uploadData.imageData}
              imageDimensions={imageDimensions}
              rows={rows}
              columns={columns}
              pieceStyle={pieceStyle}
              onRowsChange={setRows}
              onColumnsChange={setColumns}
              onPieceStyleChange={setPieceStyle}
            />
          )}

          {/* Schedule Date (Required) */}
          <motion.div className="mt-4" variants={itemVariants}>
            <label className="block text-sm font-medium text-puzzle-text mb-2">
              Schedule for Date *
            </label>
            <p className="text-xs text-puzzle-text-secondary mb-3">
              Select a date to automatically schedule this image as the daily puzzle.
            </p>
            <Calendar
              onChange={setScheduleDate}
              value={scheduleDate}
              minDate={new Date()}
              className="mx-auto"
            />
            {scheduleDate && (
              <div className="mt-2 flex items-center justify-between">
                <p className="text-sm text-puzzle-success">
                  Will be scheduled for:
                  {' '}
                  {scheduleDate.toLocaleDateString()}
                </p>
                <button
                  type="button"
                  onClick={() => setScheduleDate(null)}
                  className="text-sm text-puzzle-text-secondary hover:text-puzzle-text"
                >
                  Clear
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>

        {/* Fixed Footer */}
        <motion.div
          className="p-6 pt-4 border-t border-puzzle-border flex justify-end gap-3"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <motion.button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-puzzle-text hover:bg-puzzle-background rounded-lg transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Cancel
          </motion.button>
          <motion.button
            type="button"
            onClick={handleUpload}
            disabled={isUploading || !uploadData.title || !uploadData.imageData || !scheduleDate}
            className="px-4 py-2 bg-puzzle-blue text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </motion.button>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

ImageUploadModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default ImageUploadModal;
